import { useEffect, useRef, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { useTenant } from "@/context/tenant-context";
import { agentTasksApi, type AgentTask } from "@/api/agent-tasks";
import { agentChannelsApi } from "@/api/agent-channels";
import { queryKeys } from "@/lib/query-keys";
import { cn } from "@/lib/utils";
import {
  Plus,
  Play,
  Pause,
  Trash2,
  Clock,
  RefreshCw,
  Zap,
  CheckCircle2,
  XCircle,
  Pencil,
  X,
  Save,
} from "lucide-react";

// ── Cron expression to human-readable ────────────────────────────────
const CRON_PRESETS: Record<string, string> = {
  "* * * * *": "Every minute",
  "0 * * * *": "Every hour",
  "0 0 * * *": "Daily at midnight",
  "0 9 * * *": "Daily at 09:00",
  "0 9 * * 1-5": "Weekdays at 09:00",
  "0 17 * * 1-5": "Weekdays at 17:00",
  "0 0 * * 0": "Weekly (Sunday midnight)",
  "0 0 * * 1": "Weekly (Monday midnight)",
  "0 0 1 * *": "Monthly (1st, midnight)",
};

function cronToText(expr: string | null | unknown): string {
  if (!expr) return "—";
  // Guard against object values stored before normalisation fix
  if (typeof expr === "object") {
    const obj = expr as Record<string, unknown>;
    const val = obj.expression ?? obj.cron ?? obj.schedule ?? obj.value ?? obj.every;
    if (typeof val === "string") return cronToText(val);
    return "—";
  }
  if (typeof expr !== "string") return "—";
  if (expr === "[object Object]") return "—";
  if (CRON_PRESETS[expr]) return CRON_PRESETS[expr];
  const parts = expr.split(" ");
  if (parts.length !== 5) return expr;
  const [min, hr, dom, mon, dow] = parts;
  if (dom === "*" && mon === "*" && dow === "*" && min !== "*" && hr !== "*")
    return `Daily at ${hr.padStart(2, "0")}:${min.padStart(2, "0")}`;
  return expr;
}

function relativeTime(dateStr: string | null): string {
  if (!dateStr) return "—";
  const date = new Date(dateStr);
  const now = Date.now();
  const diff = now - date.getTime();
  if (diff < 0) {
    const absDiff = -diff;
    if (absDiff < 60_000) return "in <1m";
    if (absDiff < 3_600_000) return `in ${Math.round(absDiff / 60_000)}m`;
    if (absDiff < 86_400_000) return `in ${Math.round(absDiff / 3_600_000)}h`;
    return `in ${Math.round(absDiff / 86_400_000)}d`;
  }
  if (diff < 60_000) return "<1m ago";
  if (diff < 3_600_000) return `${Math.round(diff / 60_000)}m ago`;
  if (diff < 86_400_000) return `${Math.round(diff / 3_600_000)}h ago`;
  return `${Math.round(diff / 86_400_000)}d ago`;
}

const statusVariant: Record<string, "success" | "warning" | "danger" | "default" | "secondary"> = {
  active: "success",
  paused: "warning",
  completed: "secondary",
  failed: "danger",
};

type Filter = "all" | "recurring" | "one_off";

// ── Create task form ─────────────────────────────────────────────────
function CreateTaskForm({
  agentId,
  companyId,
  onDone,
}: {
  agentId: string;
  companyId: string;
  onDone: () => void;
}) {
  const queryClient = useQueryClient();
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [type, setType] = useState<"recurring" | "one_off">("recurring");
  const [cronExpression, setCronExpression] = useState("0 9 * * *");
  const [timezone, setTimezone] = useState("UTC");
  const [taskPrompt, setTaskPrompt] = useState("");
  const [announceChannelId, setAnnounceChannelId] = useState<string | null>(null);

  const { data: channels = [] } = useQuery({
    queryKey: ["agent-channels", agentId],
    queryFn: () => agentChannelsApi.list(agentId),
  });

  const createMutation = useMutation({
    mutationFn: () =>
      agentTasksApi.create({
        agentId,
        companyId,
        type,
        title,
        description: description || undefined,
        cronExpression: type === "recurring" ? cronExpression : undefined,
        timezone,
        taskPrompt: taskPrompt || undefined,
        announceChannelId,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.agentTasks.byAgent(agentId) });
      onDone();
    },
  });

  return (
    <div className="rounded-lg border border-border bg-background p-4">
      <div className="flex items-center gap-2 mb-3">
        <h3 className="text-sm font-semibold text-text-primary">New Task</h3>
        <div className="flex gap-1 ml-auto">
          {(["recurring", "one_off"] as const).map((t) => (
            <button
              key={t}
              onClick={() => setType(t)}
              className={cn(
                "px-2.5 py-1 rounded-md text-[11px] font-medium transition-colors",
                type === t
                  ? t === "recurring"
                    ? "bg-purple-500/15 text-purple-400"
                    : "bg-blue-500/15 text-blue-400"
                  : "bg-surface text-text-muted hover:text-text-primary",
              )}
            >
              {t === "recurring" ? "Recurring" : "One-off"}
            </button>
          ))}
        </div>
      </div>

      <div className="space-y-2">
        <Input
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder="Task title"
          className="text-sm"
        />
        <Input
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder="Description (optional)"
          className="text-sm"
        />
        {type === "recurring" && (
          <div className="flex gap-2">
            <select
              value={cronExpression}
              onChange={(e) => setCronExpression(e.target.value)}
              className="flex-1 rounded-md border border-border bg-surface px-3 py-2 text-xs"
            >
              <option value="0 * * * *">Every hour</option>
              <option value="0 9 * * *">Daily at 09:00</option>
              <option value="0 9 * * 1-5">Weekdays at 09:00</option>
              <option value="0 0 * * 1">Weekly (Monday)</option>
              <option value="0 0 1 * *">Monthly (1st)</option>
            </select>
            <select
              value={timezone}
              onChange={(e) => setTimezone(e.target.value)}
              className="rounded-md border border-border bg-surface px-3 py-2 text-xs"
            >
              <option value="UTC">UTC</option>
              <option value="Europe/London">London</option>
              <option value="America/New_York">New York</option>
              <option value="America/Los_Angeles">Los Angeles</option>
            </select>
          </div>
        )}
        <textarea
          value={taskPrompt}
          onChange={(e) => setTaskPrompt(e.target.value)}
          placeholder="Task prompt — what should the agent do when this task runs?"
          className="w-full rounded-md border border-border bg-surface px-3 py-2 text-xs min-h-[60px] resize-y"
        />
        {channels.length > 0 && (
          <div>
            <label className="text-[11px] text-text-muted mb-1 block">Announce results to</label>
            <select
              value={announceChannelId ?? ""}
              onChange={(e) => setAnnounceChannelId(e.target.value || null)}
              className="w-full rounded-md border border-border bg-surface px-3 py-2 text-xs"
            >
              <option value="">Task Reports (default)</option>
              {channels.map((ch) => (
                <option key={ch.id} value={ch.id}>
                  #{ch.name}
                </option>
              ))}
            </select>
          </div>
        )}
      </div>

      <div className="flex gap-2 mt-3">
        <Button size="sm" variant="ghost" onClick={onDone}>
          Cancel
        </Button>
        <Button
          size="sm"
          disabled={!title.trim() || createMutation.isPending}
          onClick={() => createMutation.mutate()}
        >
          {createMutation.isPending ? "Creating..." : "Create Task"}
        </Button>
      </div>
    </div>
  );
}

// ── Task row ─────────────────────────────────────────────────────────
function TaskRow({
  task,
  agentId,
}: {
  task: AgentTask;
  agentId: string;
}) {
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState(false);
  const [editTitle, setEditTitle] = useState(task.title);
  const [editDescription, setEditDescription] = useState(task.description ?? "");
  const [editCron, setEditCron] = useState(
    task.cronExpression && task.cronExpression !== "[object Object]"
      ? task.cronExpression
      : "0 9 * * *",
  );
  const [editTimezone, setEditTimezone] = useState(task.timezone ?? "UTC");
  const [editPrompt, setEditPrompt] = useState(task.taskPrompt ?? "");
  const [editAnnounceChannelId, setEditAnnounceChannelId] = useState<string | null>(task.announceChannelId);

  const { data: channels = [] } = useQuery({
    queryKey: ["agent-channels", agentId],
    queryFn: () => agentChannelsApi.list(agentId),
    enabled: editing,
  });

  const triggerMutation = useMutation({
    mutationFn: () => agentTasksApi.trigger(task.id),
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: queryKeys.agentTasks.byAgent(agentId) }),
  });

  const toggleMutation = useMutation({
    mutationFn: () =>
      agentTasksApi.update(task.id, {
        enabled: !task.enabled,
        status: task.enabled ? "paused" : "active",
      }),
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: queryKeys.agentTasks.byAgent(agentId) }),
  });

  const deleteMutation = useMutation({
    mutationFn: () => agentTasksApi.delete(task.id),
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: queryKeys.agentTasks.byAgent(agentId) }),
  });

  const updateMutation = useMutation({
    mutationFn: () =>
      agentTasksApi.update(task.id, {
        title: editTitle,
        description: editDescription || null,
        cronExpression: task.type === "recurring" ? editCron : undefined,
        timezone: editTimezone,
        taskPrompt: editPrompt || null,
        announceChannelId: editAnnounceChannelId,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.agentTasks.byAgent(agentId) });
      setEditing(false);
    },
  });

  const resultIcon = task.lastResult?.success === true
    ? <CheckCircle2 size={12} className="text-emerald-400" />
    : task.lastResult?.success === false
      ? <XCircle size={12} className="text-red-400" />
      : null;

  if (editing) {
    return (
      <div className="px-4 py-3 border-b border-border bg-surface/50 last:border-0">
        <div className="space-y-2">
          <Input
            value={editTitle}
            onChange={(e) => setEditTitle(e.target.value)}
            placeholder="Task title"
            className="text-sm"
          />
          <Input
            value={editDescription}
            onChange={(e) => setEditDescription(e.target.value)}
            placeholder="Description (optional)"
            className="text-sm"
          />
          {task.type === "recurring" && (
            <div className="flex gap-2">
              <Input
                value={editCron}
                onChange={(e) => setEditCron(e.target.value)}
                placeholder="Cron expression"
                className="flex-1 text-xs font-mono"
              />
              <select
                value={editTimezone}
                onChange={(e) => setEditTimezone(e.target.value)}
                className="rounded-md border border-border bg-surface px-3 py-2 text-xs"
              >
                <option value="UTC">UTC</option>
                <option value="Europe/London">London</option>
                <option value="America/New_York">New York</option>
                <option value="America/Los_Angeles">Los Angeles</option>
              </select>
            </div>
          )}
          <textarea
            value={editPrompt}
            onChange={(e) => setEditPrompt(e.target.value)}
            placeholder="Task prompt"
            className="w-full rounded-md border border-border bg-surface px-3 py-2 text-xs min-h-[60px] resize-y"
          />
          {channels.length > 0 && (
            <div>
              <label className="text-[11px] text-text-muted mb-1 block">Announce results to</label>
              <select
                value={editAnnounceChannelId ?? ""}
                onChange={(e) => setEditAnnounceChannelId(e.target.value || null)}
                className="w-full rounded-md border border-border bg-surface px-3 py-2 text-xs"
              >
                <option value="">Task Reports (default)</option>
                {channels.map((ch) => (
                  <option key={ch.id} value={ch.id}>
                    #{ch.name}
                  </option>
                ))}
              </select>
            </div>
          )}
        </div>
        <div className="flex gap-2 mt-3">
          <Button
            size="sm"
            variant="ghost"
            onClick={() => {
              setEditing(false);
              setEditTitle(task.title);
              setEditDescription(task.description ?? "");
              setEditCron(task.cronExpression && task.cronExpression !== "[object Object]" ? task.cronExpression : "0 9 * * *");
              setEditTimezone(task.timezone ?? "UTC");
              setEditPrompt(task.taskPrompt ?? "");
              setEditAnnounceChannelId(task.announceChannelId);
            }}
          >
            <X size={12} className="mr-1" />
            Cancel
          </Button>
          <Button
            size="sm"
            disabled={!editTitle.trim() || updateMutation.isPending}
            onClick={() => updateMutation.mutate()}
          >
            <Save size={12} className="mr-1" />
            {updateMutation.isPending ? "Saving..." : "Save"}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex items-center gap-3 px-4 py-3 border-b border-border last:border-0 hover:bg-surface-hover transition-colors">
      {/* Type indicator */}
      <div className="shrink-0">
        {task.type === "recurring" ? (
          <RefreshCw size={14} className="text-purple-400" />
        ) : (
          <Zap size={14} className="text-blue-400" />
        )}
      </div>

      {/* Title + schedule */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <p className="text-sm font-medium text-text-primary truncate">
            {task.title}
          </p>
          <Badge
            variant={statusVariant[task.status] ?? "default"}
            className="text-[10px] px-1.5 py-0"
          >
            {task.status}
          </Badge>
          {task.createdBy === "agent" && (
            <Badge variant="secondary" className="text-[10px] px-1.5 py-0">
              agent-created
            </Badge>
          )}
          {task.announceChannelName && (
            <Badge variant="outline" className="text-[10px] px-1.5 py-0">
              #{task.announceChannelName}
            </Badge>
          )}
        </div>
        {task.type === "recurring" && task.cronExpression && (
          <p className="text-xs text-text-muted mt-0.5">
            <Clock size={10} className="inline mr-1" />
            {cronToText(task.cronExpression)}
            {task.timezone !== "UTC" && ` (${task.timezone})`}
          </p>
        )}
      </div>

      {/* Last run */}
      <div className="shrink-0 text-right w-24">
        <p className="text-[11px] text-text-muted">Last run</p>
        <div className="flex items-center justify-end gap-1">
          {resultIcon}
          <p className="text-xs text-text-secondary">
            {relativeTime(task.lastRunAt)}
          </p>
        </div>
      </div>

      {/* Next run */}
      {task.type === "recurring" && (
        <div className="shrink-0 text-right w-20">
          <p className="text-[11px] text-text-muted">Next</p>
          <p className="text-xs text-text-secondary">
            {task.enabled ? relativeTime(task.nextRun) : "—"}
          </p>
        </div>
      )}

      {/* Result summary */}
      <div className="shrink-0 w-36 text-right">
        {task.lastResult?.summary ? (
          <p className="text-[11px] text-text-muted truncate" title={task.lastResult.summary}>
            {task.lastResult.summary.slice(0, 50)}
          </p>
        ) : task.lastResult?.error ? (
          <p className="text-[11px] text-red-400 truncate" title={task.lastResult.error}>
            {task.lastResult.error.slice(0, 50)}
          </p>
        ) : null}
      </div>

      {/* Actions */}
      <div className="shrink-0 flex items-center gap-1">
        <button
          onClick={() => setEditing(true)}
          className="p-1.5 rounded-md text-text-muted hover:text-accent hover:bg-accent/10 transition-colors"
          title="Edit"
        >
          <Pencil size={12} />
        </button>
        <button
          onClick={() => triggerMutation.mutate()}
          disabled={triggerMutation.isPending}
          className="p-1.5 rounded-md text-text-muted hover:text-accent hover:bg-accent/10 transition-colors"
          title="Run now"
        >
          <Play size={12} />
        </button>
        {task.type === "recurring" && (
          <button
            onClick={() => toggleMutation.mutate()}
            disabled={toggleMutation.isPending}
            className="p-1.5 rounded-md text-text-muted hover:text-text-primary hover:bg-surface-hover transition-colors"
            title={task.enabled ? "Pause" : "Resume"}
          >
            {task.enabled ? <Pause size={12} /> : <Play size={12} />}
          </button>
        )}
        <button
          onClick={() => {
            if (confirm(`Delete task "${task.title}"?`)) deleteMutation.mutate();
          }}
          disabled={deleteMutation.isPending}
          className="p-1.5 rounded-md text-text-muted hover:text-red-400 hover:bg-red-500/10 transition-colors"
          title="Delete"
        >
          <Trash2 size={12} />
        </button>
      </div>
    </div>
  );
}

// ── Sync button ──────────────────────────────────────────────────────
function SyncButton({ agentId }: { agentId: string }) {
  const queryClient = useQueryClient();
  const [result, setResult] = useState<{ created: number; updated: number; completed: number } | null>(null);

  const syncMutation = useMutation({
    mutationFn: () => agentTasksApi.syncFromAgent(agentId),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.agentTasks.byAgent(agentId) });
      setResult(data);
      setTimeout(() => setResult(null), 5000);
    },
  });

  return (
    <div className="flex items-center gap-2">
      {result && (
        <span className="text-[11px] text-text-muted">
          {result.created > 0 ? `+${result.created}` : ""}
          {result.updated > 0 ? ` ~${result.updated}` : ""}
          {result.created === 0 && result.updated === 0 ? "Up to date" : " synced"}
        </span>
      )}
      <Button
        size="sm"
        variant="outline"
        disabled={syncMutation.isPending}
        onClick={() => syncMutation.mutate()}
      >
        <RefreshCw size={12} className={cn("mr-1", syncMutation.isPending && "animate-spin")} />
        {syncMutation.isPending ? "Syncing..." : "Sync from Agent"}
      </Button>
    </div>
  );
}

// ── Main component ───────────────────────────────────────────────────
export function AgentTasksTab({ agentId }: { agentId: string }) {
  const { tenantId } = useTenant();
  const queryClient = useQueryClient();
  const [filter, setFilter] = useState<Filter>("all");
  const [showCreate, setShowCreate] = useState(false);

  const { data: tasks = [], isLoading } = useQuery({
    queryKey: queryKeys.agentTasks.byAgent(agentId),
    queryFn: () => agentTasksApi.list(tenantId, { agentId }),
  });

  // Auto-sync on mount for OpenClaw agents: their cron jobs live on the VM
  // and don't land in agent_tasks until syncFromAgent is called. The server
  // no-ops this for Native agents, so firing unconditionally is safe. We
  // key the ref on agentId so switching between agents re-fires once each.
  const lastSyncedAgentId = useRef<string | null>(null);
  useEffect(() => {
    if (lastSyncedAgentId.current === agentId) return;
    lastSyncedAgentId.current = agentId;
    agentTasksApi
      .syncFromAgent(agentId)
      .then(() =>
        queryClient.invalidateQueries({
          queryKey: queryKeys.agentTasks.byAgent(agentId),
        }),
      )
      .catch(() => {
        // Non-fatal: user still sees whatever's already in agent_tasks,
        // and the manual "Sync from Agent" button is still available.
      });
  }, [agentId, queryClient]);

  const filtered = filter === "all" ? tasks : tasks.filter((t) => t.type === filter);

  return (
    // Same frame as the Access tab: p-6 inset, a heading row with the actions
    // on the right, then the list in one bordered box.
    <div className="h-full space-y-3 overflow-y-auto p-6">
      <div className="flex items-center gap-3">
        <h3 className="text-sm font-medium text-text-primary">
          Tasks
          {!isLoading && <span className="ml-1.5 font-normal text-text-muted">· {tasks.length}</span>}
        </h3>

        {/* Filters */}
        <div className="flex gap-1 ml-2">
          {(
            [
              ["all", "All"],
              ["recurring", "Recurring"],
              ["one_off", "One-off"],
            ] as const
          ).map(([key, label]) => (
            <button
              key={key}
              onClick={() => setFilter(key)}
              className={cn(
                "px-2.5 py-1 rounded-md text-[11px] font-medium transition-colors",
                filter === key
                  ? "bg-accent/10 text-accent"
                  : "text-text-muted hover:text-text-primary",
              )}
            >
              {label}
              {key !== "all" && (
                <span className="ml-1 opacity-60">
                  ({tasks.filter((t) => t.type === key).length})
                </span>
              )}
            </button>
          ))}
        </div>

        <div className="ml-auto flex items-center gap-2">
          <SyncButton agentId={agentId} />
          <Button size="sm" onClick={() => setShowCreate(!showCreate)}>
            <Plus size={14} className="mr-1" />
            New Task
          </Button>
        </div>
      </div>

      {showCreate && (
        <CreateTaskForm
          agentId={agentId}
          companyId={tenantId}
          onDone={() => setShowCreate(false)}
        />
      )}

      {isLoading ? (
        <p className="text-sm text-text-muted">Loading...</p>
      ) : (
        <div className="overflow-hidden rounded-lg border border-border bg-surface">
          {filtered.length === 0 ? (
            // No "Create first task" button here: New Task above does it.
            <p className="px-4 py-3 text-xs text-text-muted">
              {filter === "all"
                ? "No tasks yet."
                : `No ${filter === "recurring" ? "recurring" : "one-off"} tasks.`}
            </p>
          ) : (
            filtered.map((task) => (
              <TaskRow key={task.id} task={task} agentId={agentId} />
            ))
          )}
        </div>
      )}
    </div>
  );
}
